import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { createContext, useContext, useState } from "react";

/** How `LearnSummaryHost` is showing this summary. */
export type LearnSummaryPresence = {
  /** Start committed: keep this summary above the deck until its card has flown off. */
  depart: () => void;
  /** The summary is Learn's current route and the Learn tab is in front. */
  isCurrent: boolean;
  /** Start committed; the card is flying off over the deck, which already took over. */
  isDeparting: boolean;
  /** Learn's map is open over this summary, which keeps its place (and its controls) beneath it. */
  isUnderMap: boolean;
  /** Start failed to open the deck: the summary stays, and no longer outlives its route. */
  stay: () => void;
};

/** Provided by `LearnSummaryHost`. Absent when a test renders the summary on a route of its own. */
export const LearnSummaryPresenceContext = createContext<LearnSummaryPresence | null>(null);

/** Whether this summary is the screen the learner is looking at. */
export function useLearnSummaryIsCurrent() {
  const presence = useContext(LearnSummaryPresenceContext);
  const isFocused = useIsFocused();
  return presence?.isCurrent ?? isFocused;
}

/** Whether Learn's map is open over this summary. */
export function useLearnSummaryIsUnderMap() {
  return useContext(LearnSummaryPresenceContext)?.isUnderMap ?? false;
}

/**
 * `value`, except that a departing summary keeps what it showed when Start committed: the card
 * flies off over the deck exactly as the learner pressed it, while Start's own effects (such as
 * hiding the launch greeting) land underneath.
 */
export function useLearnSummaryHeldValue<T>(value: T): T {
  const isDeparting = useContext(LearnSummaryPresenceContext)?.isDeparting ?? false;
  const [held, setHeld] = useState(() => value);
  if (!isDeparting && !Object.is(held, value)) {
    setHeld(() => value);
  }
  return isDeparting ? held : value;
}
